/* eslint max-len: 0 */
/* eslint-disable camelcase */
// If comments exceeded screen width have text editor auto-wrap
import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Translation } from 'react-i18next';
import CustomTemplate from '../customtemplate/customtemplate.jsx';
import getTemplate from '../../utils/gettemplate';
import './location.scss';

const Location = (props) => {
  // Variables that control display behavior
  let isActive = false;
  const locationRef = React.createRef();

  // Prop variables passed by parent
  const {
    // eslint-disable-next-line camelcase
    phonemap, phonemap_e164, distance, street, street2, city, state, postal_code,
  } = props.data;
  const locationUrl = props.data.location_url;
  const locationName = props.data.location_name;
  const services = (props.data.services ? JSON.parse(props.data.services) : null);

  // Constructed variables
  const todayDayName = i18n.t('DaysOfTheWeek', { returnObjects: true })[new Date().getDay()];
  const roundedDistance = distance.toFixed(2);

  // Static variables
  const distanceUnit = 'mi';

  // Element variables
  const Template = getTemplate(props, 'location-item');
  let LocationTemplateHtml;
  let LocationNameElement;

  // Function that allows child component to manage state in map.jsx
  const handleSelection = () => {
    props.toggleDisplayInfoWindow();
    props.setSelectedLocationId(props.data.id.toString());
    props.setCenterPosition({
      centerLatitude: props.data.lat,
      centerLongitude: props.data.lon,
    });
  };

  // Function that manages browser display behavior
  // scrolls currently selected business location into view in location list
  // This behavior could be shifted to a higher level
  const handleScrollIntoView = () => {
    if (locationRef.current) {
      const locationFinder = document.querySelector('.sb-locationfinder');
      const boundingBox = locationFinder.getBoundingClientRect();
      // Checks to see if locationfinder is in user viewport
      // This prevents page from automatically scrolling to location finder when it is incorporated into the page.
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect
      if (
        boundingBox.bottom > 0 
        && boundingBox.right > 0 
        && boundingBox.left < (window.innerWidth || document.documentElement.clientWidth)
        && boundingBox.top < (window.innerHeight || document.documentElement.clientHeight)
      ) {
        locationRef.current.scrollIntoView({
          block: 'nearest',
        });
      }
    }
  };

  // Set conditional and static HTML
  // Custom template for the location list item
  if (Template) {
    const templateContext = {
      location: props.data,
      distance: roundedDistance,
      services,
      todayDayName,
    };
    LocationTemplateHtml = <CustomTemplate template={Template} context={templateContext} />;
  }

  /* Default location block */
  if (locationName) {
    LocationNameElement = <div className="sb-location-name" role="heading" aria-level="5">{locationName}</div>;
  }

  const DefaultLocationDetail = (
    <>
      {LocationNameElement}
      <div className="sb-location-distance" aria-label={i18n.t('distance')}>
        {roundedDistance} {distanceUnit}
      </div>
      <div className="sb-location-address" aria-label={i18n.t('streetAddress')}>
        {street} {street2}<br/>{city}, {state} {postal_code}
      </div>
      <div className="sb-location-phone">
        <a href={`tel:${phonemap_e164.phone}`} aria-label={i18n.t('phoneNumber')}>{phonemap.phone}</a>
      </div>
      <div className="sb-location-links">
        <span className="sb-location-link sb-location-link-view-location">
          <a href={locationUrl} rel="noopener noreferrer" aria-label={i18n.t('locationLink')}>
            <Translation>{(t) => t('View Location') }</Translation>
          </a>
        </span>
        <span className="sb-location-link sb-location-link-get-directions">
          <a href={`https://www.google.com/maps/dir/?api=1&destination=${props.data.formatted_address}`} aria-label={i18n.t('directionsLink')} rel="noopener noreferrer" target="_blank">
            <Translation>{(t) => t('Get Directions') }</Translation>
          </a>
        </span>
      </div>
    </>
  );

  // Manage browser window positioning
  if (parseInt(props.data.id, 10) === parseInt(props.selectedLocationId, 10)) {
    handleScrollIntoView();
    isActive = true;
  } else {
    isActive = false;
  }

  return (
    <div
      aria-label={`${i18n.t('location')}-${locationName}`}
      className={ `${isActive ? 'sb-location sb-location-active' : 'sb-location'} ${props.data.matches_filters ? 'sb-location-matches-filters' : ''}` }
      id={`location-${props.data.id}`}
      onClick={handleSelection}
      ref={locationRef}
      role="listitem"
    >
      {LocationTemplateHtml || DefaultLocationDetail}
    </div>
  );
};

Location.propTypes = {
  data: PropTypes.object,
  selectedLocationId: PropTypes.string,
  setCenterPosition: PropTypes.func,
  setSelectedLocationId: PropTypes.func,
  toggleDisplayInfoWindow: PropTypes.func,
};

export default Location;
